import { debugLog } from "../../../utils/debug";

export function buildDenseMesh(
  disparity,
  calibration,
  { maximumRelativeDepthJump = 0.08 } = {},
) {
  if (!disparity || !calibration) {
    return null;
  }

  const { width, height, disparities, confidence } = disparity;
  const { cameraMatrixA, baseline, disparityOffset } = calibration;

  const fx = cameraMatrixA.get(0, 0);
  const fy = cameraMatrixA.get(1, 1);
  const cx = cameraMatrixA.get(0, 2);
  const cy = cameraMatrixA.get(1, 2);

  // One vertex slot per image pixel preserves direct image-grid connectivity.
  const positions = new Float32Array(width * height * 3);
  const uvs = new Float32Array(width * height * 2);
  const vertexConfidence = new Float32Array(width * height);
  const valid = new Uint8Array(width * height);

  positions.fill(0);

  let vertexCount = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const disparityValue = disparities[index];

      if (!Number.isFinite(disparityValue)) {
        continue;
      }

      const correctedDisparity = disparityValue + disparityOffset;

      const denominator = calibration.useAbsoluteDisparity
        ? Math.abs(correctedDisparity)
        : correctedDisparity;

      if (denominator <= 0) {
        continue;
      }

      // Rectified stereo depth: Z = fB / d.
      const z = (fx * baseline) / denominator;

      // Very small disparities produce large, unstable depths.
      const maximumDepth = 80;

      // if (!Number.isFinite(z) || z <= 0 || z > maximumDepth) {
      //   continue;
      // }

      if (!Number.isFinite(z) || z <= 0) {
        continue;
      }

      const worldX = ((x - cx) * z) / fx;
      const worldY = ((y - cy) * z) / fy;

      if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) {
        continue;
      }

      const positionIndex = index * 3;

      positions[positionIndex] = -worldX;
      positions[positionIndex + 1] = -worldY;
      positions[positionIndex + 2] = z;

      const uvIndex = index * 2;

      uvs[uvIndex] = x / Math.max(1, width - 1);
      uvs[uvIndex + 1] = 1 - y / Math.max(1, height - 1);

      vertexConfidence[index] =
        confidence && Number.isFinite(confidence[index])
          ? confidence[index]
          : 1;

      valid[index] = 1;
      vertexCount += 1;
    }
  }

  const indices = [];

  let rejectedMissingVertex = 0;
  let rejectedInvalidDepth = 0;
  let rejectedDepthJump = 0;

  function triangleIsContinuous(a, b, c) {
    if (!valid[a] || !valid[b] || !valid[c]) {
      rejectedMissingVertex += 1;
      return false;
    }

    const za = positions[a * 3 + 2];
    const zb = positions[b * 3 + 2];
    const zc = positions[c * 3 + 2];

    const minimumDepth = Math.min(za, zb, zc);
    const maximumDepth = Math.max(za, zb, zc);

    if (minimumDepth <= 0) {
      rejectedInvalidDepth += 1;
      return false;
    }

    // Reject triangles crossing large relative depth discontinuities.
    const relativeDepthJump = (maximumDepth - minimumDepth) / minimumDepth;

    if (relativeDepthJump > maximumRelativeDepthJump) {
      rejectedDepthJump += 1;
      return false;
    }

    return true;
  }

  let rejectedTriangles = 0;

  for (let y = 0; y < height - 1; y += 1) {
    for (let x = 0; x < width - 1; x += 1) {
      const topLeft = y * width + x;
      const topRight = topLeft + 1;
      const bottomLeft = topLeft + width;
      const bottomRight = bottomLeft + 1;

      // Split each image-grid quad along the TL -> BR diagonal.
      if (triangleIsContinuous(topLeft, bottomLeft, bottomRight)) {
        indices.push(topLeft, bottomLeft, bottomRight);
      } else {
        rejectedTriangles += 1;
      }

      if (triangleIsContinuous(topLeft, bottomRight, topRight)) {
        indices.push(topLeft, bottomRight, topRight);
      } else {
        rejectedTriangles += 1;
      }
    }
  }

  const triangleCount = indices.length / 3;
  const candidateTriangles = triangleCount + rejectedTriangles;

  debugLog("[Reconstruction] Dense mesh:", {
    vertices: vertexCount,
    candidateTriangles,
    triangles: triangleCount,
    rejectedTriangles,
    rejectedMissingVertex,
    rejectedInvalidDepth,
    rejectedDepthJump,
    acceptedPercent:
      candidateTriangles > 0
        ? ((triangleCount / candidateTriangles) * 100).toFixed(1)
        : "0.0",
    maximumRelativeDepthJump,
  });

  return {
    width,
    height,
    positions,
    uvs,
    confidence: vertexConfidence,
    indices: new Uint32Array(indices),
    valid,
    vertexCount,
    triangleCount,
  };
}
